import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Cookie, Shield } from "lucide-react";

const COOKIE_CONSENT_KEY = "lackzentrum_cookie_consent";

export const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay showing banner slightly for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (consentData) => {
    const consentRecord = {
      ...consentData,
      timestamp: new Date().toISOString(),
      version: "1.0"
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentRecord));
    setShowBanner(false);
  };

  const acceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true
    });
  };

  const acceptNecessary = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false
    });
  };

  const savePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6">
      <div className="container mx-auto max-w-4xl">
        <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-accent/5 px-6 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Cookie className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-foreground">
                  Cookie-Einstellungen
                </h3>
                <p className="text-xs text-muted-foreground">
                  Wir respektieren Ihre Privatsphäre
                </p>
              </div>
            </div>
            <button
              onClick={acceptNecessary}
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5">
            <p className="text-muted-foreground text-sm leading-relaxed mb-4">
              Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
              Einige Cookies sind für den Betrieb der Website unbedingt erforderlich, während andere 
              uns helfen, diese Website und Ihre Erfahrung zu verbessern.
            </p>

            {/* Cookie Details Toggle */}
            {showDetails && (
              <div className="mb-5 space-y-4 border border-border rounded-lg p-4 bg-muted/30">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="h-4 w-4 text-primary" />
                      <span className="font-medium text-foreground text-sm">
                        Notwendige Cookies
                      </span>
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                        Erforderlich
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.necessary}
                    disabled
                    className="h-5 w-5 rounded accent-primary cursor-not-allowed"
                  />
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between gap-4 pt-3 border-t border-border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground text-sm">
                        Analyse-Cookies
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer Website interagieren.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({...preferences, analytics: e.target.checked})}
                    className="h-5 w-5 rounded accent-primary cursor-pointer"
                  />
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between gap-4 pt-3 border-t border-border">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-foreground text-sm">
                        Marketing-Cookies
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen (Google Maps).
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({...preferences, marketing: e.target.checked})}
                    className="h-5 w-5 rounded accent-primary cursor-pointer"
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDetails(!showDetails)}
                className="order-3 sm:order-1"
              >
                {showDetails ? "Details ausblenden" : "Details anzeigen"}
              </Button>
              
              {showDetails ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={savePreferences}
                  className="order-1 sm:order-2 sm:ml-auto"
                >
                  Auswahl speichern
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={acceptNecessary}
                    className="order-2 sm:ml-auto"
                  >
                    Nur notwendige
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={acceptAll}
                    className="order-1"
                  >
                    Alle akzeptieren
                  </Button>
                </>
              )}
            </div>

            {/* Legal Links */}
            <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs">
              <a 
                href="/datenschutz" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Datenschutzerklärung
              </a>
              <a 
                href="/impressum" 
                className="text-muted-foreground hover:text-primary transition-colors"
              >
                Impressum
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
